import { CloudDownload, Renew } from '@carbon/icons-react';
import { Content, Section, Heading, Tile, Grid, Column, Button } from '@carbon/react';
import { getSystemName } from '#app/utils/system-name.server.ts';
import { type Route } from './+types/deployment-maintenance.ts';

export async function loader(): Promise<{ systemName: string }> {
  // During deployment, avoid database calls that might fail
  // Use a static fallback instead
  try {
    const systemName = await getSystemName();
    return { systemName };
  } catch (error) {
    // If getSystemName fails (e.g., during deployment), use fallback
    return { systemName: 'CircuitBread Insights' };
  }
}

export default function DeploymentMaintenance({
  loaderData,
}: Route.ComponentProps) {
  const { systemName } = loaderData;

  const handleRefresh = () => {
    window.location.reload();
  };

  const handleRetry = () => {
    // Go back to previous page or home
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  return (
    <>
      <title>{`Deployment in Progress - ${systemName}`}</title>
      <meta name="robots" content="noindex, nofollow" />
      <Content className="!mt-0 min-h-screen bg-gray-900">
        <Grid>
          <Column sm={4} md={8} lg={16}>
            <Section className="flex justify-center mt-40">
              <div className="mx-auto w-full max-w-lg">
                <Tile className="p-8">
                  <div className="flex flex-col gap-4 text-center mb-6">
                    <div className="flex justify-center">
                      <CloudDownload size={64} className="text-blue-400" />
                    </div>

                    <div className="flex items-center justify-center gap-2 mb-4">
                      <img
                        src="/img/cb-logo-black.png"
                        alt="CircuitBread Logo"
                        className="h-8 w-auto"
                      />
                      <span className="text-2xl font-bold text-white">
                        {systemName}
                      </span>
                    </div>

                    <Heading className="text-gray-300">
                      Deployment in Progress
                    </Heading>

                    <div className="space-y-3 text-gray-400 max-w-md mx-auto">
                      <p>
                        We're currently deploying new updates to improve your experience.
                        This process typically takes 1-2 minutes.
                      </p>

                      <p className="text-sm">
                        Your data is safe and will be available once the deployment completes.
                      </p>
                    </div>

                    <div className="pt-6 space-y-4">
                      <div className="inline-flex items-center space-x-2 text-lg text-blue-300">
                        <div className="w-4 h-4 bg-blue-400 rounded-full animate-pulse"></div>
                        <span>Deployment in progress...</span>
                      </div>

                      <div className="flex gap-3 justify-center">
                        <Button
                          kind="secondary"
                          size="sm"
                          onClick={handleRefresh}
                          renderIcon={Renew}
                        >
                          Refresh Page
                        </Button>

                        <Button
                          kind="primary"
                          size="sm"
                          onClick={handleRetry}
                        >
                          Try Again
                        </Button>
                      </div>
                    </div>

                    <div className="pt-4 text-xs text-gray-500">
                      <p>If this persists for more than 5 minutes, please contact support.</p>
                    </div>
                  </div>
                </Tile>
              </div>
            </Section>
          </Column>
        </Grid>
      </Content>
    </>
  );
}